import {
  Component,
  EventEmitter,
  Input,
  Output,
  ChangeDetectionStrategy,
  inject,
} from '@angular/core';
import { AlertService, AuthService, Permissions } from '@app/core';
import { environment } from '@app/environment';
import { ListItem, MenuList } from '@app/model';
import { map } from 'rxjs/operators';
import { SideMenuLayoutComponent } from '../side-menu-layout/side-menu-layout.component';

type NavLink = {
  label: string;
  icon: string;
  link: string;
  exact?: boolean;
  /** Shows the number of unread alerts. */
  alerts?: boolean;
};

@Component({
  selector: 'app-side-menu',
  templateUrl: './side-menu.component.html',
  styleUrls: ['./side-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SideMenuComponent {
  private auth = inject(AuthService);
  private alertService = inject(AlertService, { optional: true });
  /** The layout around the menu, if any: its drawer closes after a click. */
  private layout = inject(SideMenuLayoutComponent, { optional: true });

  @Input() menu: MenuList[] = [];
  @Output() itemSelected = new EventEmitter<{
    list: number | string;
    item: ListItem;
  }>();
  @Output() actionSelected = new EventEmitter<number>();

  public readonly appName = environment.branding.appName;
  public readonly logo = environment.branding.logo;
  /** The main pages; the search has this single entry in the sidebar. */
  public readonly mainLinks: NavLink[] = [
    {
      label: 'common.nav.home',
      icon: 'fa-house',
      link: '/home',
      exact: true,
    },
    {
      label: 'common.nav.search',
      icon: 'fa-magnifying-glass',
      link: '/search',
    },
    { label: 'common.nav.lists', icon: 'fa-table-list', link: '/lists' },
    {
      label: 'common.nav.alerts',
      icon: 'fa-bell',
      link: '/alerts',
      alerts: true,
    },
  ];
  /**
   * The admin pages, pinned to the bottom of the sidebar, for the roles the
   * admin routes let in. The users page
   * is routed but has no content yet, so it has no entry.
   */
  public readonly adminLinks: NavLink[] = this.auth.checkPermission(
    Permissions.EditSectorScoringFormularWeights,
    Permissions.ManageSectorKeywords
  )
    ? [
        {
          label: 'common.nav.listManagement',
          icon: 'fa-list-check',
          link: '/admin/lists',
        },
        {
          label: 'common.nav.sectors',
          icon: 'fa-layer-group',
          link: '/admin/sectors',
        },
      ]
    : [];
  public readonly unreadAlerts$ = (this.alertService?.alerts$ ?? null)?.pipe(
    map((alerts) => alerts.filter((x) => x.unread).length)
  );

  /** The link of the entry navigates; the drawer closes. */
  listItemClicked = (list: number | string, item: ListItem) => {
    this.itemSelected.emit({ list, item });
    this.layout?.navigated();
  };

  listActionClicked = (list: number) => {
    this.actionSelected.emit(list);
  };

  linkClicked() {
    this.layout?.navigated();
  }

  /** In the icon rail a section icon expands the sidebar to its lists. */
  expandSidebar() {
    this.layout?.expand();
  }
}
